/**
 * Review chip stack — renders above the Ivy chat composer.
 *
 * Each chip = one accepted reviewer comment, with the original text shown read-only
 * and an editable "Your note" field that's what actually gets sent to Ivy.
 *
 * One CTA at the bottom: "Send N comments" → serializes the bundle into a single
 * markdown message, dispatches via the embedded chat store, clears the queue, and
 * marks the comments as 'sent' on the server.
 */

import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronDown, Send, X, Pencil } from 'lucide-react';
import { cn } from '../../lib/utils';
import { AutoResizeTextarea } from '../ui/auto-resize-textarea';
import { useReviewQueueStore, type QueuedReviewItem } from '../../stores/reviewQueueStore';
import { useEmbeddedIvyChatStore } from '../../stores/embeddedIvyChatStore';
import { buildReviewPrompt } from '../../lib/buildReviewPrompt';
import { anchorChipLabel } from '../../lib/reviewAnchor';
import { patchReviewComment } from '../../services/reviewsAPI';
import { patchAssessmentReviewComment } from '../../services/assessmentReviewsAPI';
import { ReviewerAvatar } from '../video-workspace/reviews/ReviewerAvatar';
import { QuestionButton } from '../studio/plan/Thread';
import { studioBOn } from '../../lib/studioB/flag';

interface Props {
  conversationId: string;
}

export function ReviewChipStack({ conversationId }: Props) {
  const items = useReviewQueueStore((s) => s.queuesByConversation[conversationId] ?? []);
  const updateNote = useReviewQueueStore((s) => s.updateNote);
  const remove = useReviewQueueStore((s) => s.remove);
  const clear = useReviewQueueStore((s) => s.clear);

  const [collapsed, setCollapsed] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (items.length === 0) return null;

  const handleSend = async () => {
    if (items.length === 0 || sending) return;
    setSending(true);
    setError(null);
    const messageText = buildReviewPrompt(items);
    const sentMessageId = `review-batch-${Date.now()}`;
    try {
      await useEmbeddedIvyChatStore.getState().sendMessage(messageText);
      // Mark each comment as sent in the background — failures here don't block UX.
      // Route to the right endpoint by anchor kind (video slide vs assessment question).
      await Promise.allSettled(
        items.map((it) => {
          const patch = it.anchor.kind === 'question' ? patchAssessmentReviewComment : patchReviewComment;
          return patch(conversationId, it.reviewId, it.commentId, {
            status: 'sent',
            creatorNote: it.creatorNote,
            sentMessageId,
          });
        }),
      );
      clear(conversationId);
    } catch (err: any) {
      setError(err?.message ?? 'Failed to send review');
    } finally {
      setSending(false);
    }
  };

  const b = studioBOn();
  return (
    <div data-queue-tray className={b ? 'mx-3 mt-2 rounded-[var(--b-radius-card)] border border-b-line bg-b-panel' : 'border-t border-border-secondary bg-background/60'}>
      <button
        onClick={() => setCollapsed((v) => !v)}
        aria-expanded={!collapsed}
        className={b
          ? 'flex w-full items-center justify-between gap-2 px-3.5 py-2 text-left text-b-meta text-b-t2 hover:text-b-t1'
          : 'w-full flex items-center justify-between px-3 py-1.5 text-[11px] font-semibold uppercase tracking-wider studio-text-secondary hover:studio-text-primary'}
      >
        {b ? (
          <span>{items.length} reviewer comment{items.length === 1 ? '' : 's'} to send</span>
        ) : (
          <span className="inline-flex items-center gap-1.5">
            <span className="inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full bg-amber-500 text-amber-950 text-[10px] tabular-nums">
              {items.length}
            </span>
            Reviewer comment{items.length === 1 ? '' : 's'} queued
          </span>
        )}
        <ChevronDown className={cn('w-3.5 h-3.5 transition-transform', collapsed && '-rotate-90')} />
      </button>

      <AnimatePresence initial={false}>
        {!collapsed && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.18 }}
            className="overflow-hidden"
          >
            <ul className="px-2 pb-2 space-y-1.5 max-h-[280px] overflow-y-auto">
              {items.map((item) => (
                <ReviewChip
                  key={item.commentId}
                  b={b}
                  item={item}
                  editing={editingId === item.commentId}
                  onStartEdit={() => setEditingId(item.commentId)}
                  onStopEdit={() => setEditingId(null)}
                  onChangeNote={(text) => updateNote(conversationId, item.commentId, text)}
                  onRemove={() => remove(conversationId, item.commentId)}
                />
              ))}
            </ul>
            {error && <p className="text-[11px] text-destructive px-3 pb-1.5">{error}</p>}
            {b ? (
              <div className="px-2.5 pb-2.5">
                <QuestionButton label={sending ? 'Sending…' : `Send ${items.length} to Ivy`} recommended disabled={sending} onClick={handleSend} />
              </div>
            ) : (
            <div className="px-2 pb-2">
              <button
                onClick={handleSend}
                disabled={sending}
                className="w-full inline-flex items-center justify-center gap-1.5 px-3 py-1.5 rounded-md bg-primary text-primary-foreground text-[12px] font-medium hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <Send className="w-3.5 h-3.5" />
                {sending ? 'Sending…' : `Send ${items.length} comment${items.length === 1 ? '' : 's'} to Ivy`}
              </button>
            </div>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

interface ChipProps {
  b?: boolean;
  item: QueuedReviewItem;
  editing: boolean;
  onStartEdit: () => void;
  onStopEdit: () => void;
  onChangeNote: (text: string) => void;
  onRemove: () => void;
}

function ReviewChip({ b = false, item, editing, onStartEdit, onStopEdit, onChangeNote, onRemove }: ChipProps) {
  const noteIsEdited = item.creatorNote.trim() !== item.text.trim();
  return (
    <li className={b ? 'rounded-[var(--b-radius-control)] bg-b-raised px-2.5 py-2' : 'rounded-md border border-black/5 dark:border-white/5 bg-white/40 dark:bg-white/[0.03] px-2 py-1.5'}>
      <div className="flex items-center gap-1.5">
        <ReviewerAvatar name={item.reviewerName} size={18} />
        <span className={b ? 'min-w-0 flex-1 truncate text-b-meta font-medium text-b-t1' : 'text-[11px] font-semibold studio-text-primary truncate flex-1'}>{item.reviewerName}</span>
        <span className={b ? 'flex-none text-b-meta tabular-nums text-b-t3' : 'inline-flex items-center text-[10px] studio-text-secondary font-mono tabular-nums px-1 py-0.5 rounded bg-black/[0.05] dark:bg-white/[0.08]'}>
          {anchorChipLabel(item.anchor)}
        </span>
        <button
          onClick={onRemove}
          className="p-0.5 rounded hover:bg-black/[0.06] dark:hover:bg-white/[0.08] studio-text-secondary"
          aria-label="Move back to Open"
          title="Move back to Open"
        >
          <X className="w-3 h-3" />
        </button>
      </div>
      <p className="text-[11px] text-muted-foreground italic mt-1 truncate" title={item.text}>
        “{item.text}”
      </p>
      {editing ? (
        <AutoResizeTextarea
          autoFocus
          value={item.creatorNote}
          onChange={(e) => onChangeNote(e.target.value)}
          onBlur={onStopEdit}
          minRows={2}
          maxRows={6}
          className="mt-1 w-full text-[12px] bg-transparent border border-black/10 dark:border-white/10 focus:border-black/30 dark:focus:border-white/30 rounded px-2 py-1 resize-none focus:outline-none"
          placeholder="Your note to Ivy…"
        />
      ) : (
        <button
          onClick={onStartEdit}
          className="mt-1 w-full text-left text-[12px] studio-text-primary px-2 py-1 rounded hover:bg-black/[0.04] dark:hover:bg-white/[0.05] inline-flex items-start gap-1.5"
        >
          <Pencil className="w-3 h-3 mt-0.5 flex-shrink-0 opacity-50" />
          <span className="flex-1">
            {item.creatorNote || <span className="italic opacity-60">Your note (click to edit)</span>}
            {noteIsEdited && <span className="ml-1 text-[10px] text-amber-600 dark:text-amber-400">· edited</span>}
          </span>
        </button>
      )}
    </li>
  );
}
